import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ClipboardIcon,
  CloudArrowDownIcon,
  CommandLineIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { userAppContext } from '@/context/Context';
import { useUserApiKey } from '@/hooks/useUserApiKey';
import { DUMP_MD5, DumpFormat, getDumpBaseUrl } from './dumpMetadata';

const FORMATS: {
  id: DumpFormat;
  title: string;
  description: string;
  filename: string;
  endpoint: 'download' | 'downloadParquet';
  icon: typeof TableCellsIcon;
}[] = [
  {
    id: 'tsv',
    title: 'TSV dump',
    description: 'Tab-separated files covering the full DISGENET database.',
    filename: 'disgenet_dump.tar.gz',
    endpoint: 'download',
    icon: TableCellsIcon,
  },
  {
    id: 'parquet',
    title: 'Parquet dump',
    description: 'Columnar Parquet files, ideal for analytics and data lakes.',
    filename: 'disgenet_dump_parquet.tar.gz',
    endpoint: 'downloadParquet',
    icon: CloudArrowDownIcon,
  },
];

function CopyableCode({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard may be blocked by the browser; keep UI unchanged.
    }
  };

  return (
    <button type="button" onClick={handleCopy} className="dump-code" aria-label={label}>
      <code>{value}</code>
      <span className="dump-code__icon">
        {copied ? <CheckIcon className="w-4 h-4" /> : <ClipboardIcon className="w-4 h-4" />}
      </span>
    </button>
  );
}

export default function DownloadTool() {
  const { user, userApiKey: contextUserApiKey, setUserApiKey: contextSetUserApiKey } = userAppContext();
  const { userApiKey } = useUserApiKey({
    user,
    userApiKey: contextUserApiKey,
    setUserApiKey: contextSetUserApiKey,
  });

  const [apiKey, setApiKey] = useState('');
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const dumpBase = useMemo(() => getDumpBaseUrl(), []);

  useEffect(() => {
    if (userApiKey) setApiKey(userApiKey);
  }, [userApiKey]);

  const handleDownload = (event: FormEvent<HTMLFormElement>, endpoint: string) => {
    event.preventDefault();
    const key = apiKey.trim();
    if (!key) {
      setDownloadError('Enter your API key to start the download.');
      return;
    }
    setDownloadError(null);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${dumpBase}dump/${endpoint}`;
    form.style.display = 'none';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'api-key';
    input.value = key;
    form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const curlCommands = FORMATS.map(format => ({
    id: format.id,
    label: `Copy curl command for ${format.title}`,
    value: `curl -L -d "api-key=YOUR_API_KEY" ${dumpBase}dump/${format.endpoint} -o ${format.filename}`,
  }));

  return (
    <div className="dump-download">
      <style>{`
        .dump-download .dump-feature-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-top: 16px;
          margin-bottom: 16px;
        }
        .dump-feature-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 18px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .dump-feature-card h3 {
          font-size: 15px;
          font-weight: 600;
          color: var(--blue-color);
          margin: 0;
        }
        .dump-feature-card p {
          font-size: 14px;
          margin: 0;
          flex: 1;
        }
        .dump-md5 {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12px;
          color: var(--secondary-gray-color);
          word-break: break-all;
        }
        .dump-api-row {
          display: flex;
          gap: 8px;
          margin-top: 12px;
          flex-wrap: wrap;
        }
        .dump-api-row input {
          min-width: 0;
          flex: 1;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 10px 12px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 14px;
          background: #fff;
        }
        .dump-icon-btn {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #fff;
          padding: 10px 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .dump-icon-btn:hover { border-color: var(--pink-color); color: var(--pink-color); }
        .dump-download-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin: 0 !important;
          width: 100%;
        }
        .dump-code {
          margin-top: 10px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          width: 100%;
          background: #f6f8fa;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 10px 12px;
          cursor: pointer;
          text-align: left;
        }
        .dump-code code {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 13px;
          color: var(--blue-color);
          white-space: pre-wrap;
          word-break: break-all;
          flex: 1;
        }
        .dump-code__icon { display: flex; flex-shrink: 0; color: var(--secondary-gray-color); margin-top: 2px; }
        .dump-code:hover .dump-code__icon { color: var(--pink-color); }
        .dump-steps { display: flex; flex-direction: column; margin-top: 8px; }
        .dump-step {
          display: grid;
          grid-template-columns: 32px 1fr;
          gap: 14px;
          padding: 14px 0;
          border-top: 1px solid #e5e7eb;
        }
        .dump-step:last-child { border-bottom: 1px solid #e5e7eb; }
        .dump-step__num { font-weight: 600; color: var(--pink-color); }
        .dump-step p { margin: 0; }
        .dump-error { color: #b42318; font-size: 14px; margin-top: 8px; }
        @media (max-width: 640px) {
          .dump-feature-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <p>
        Download the full DISGENET database as a collection of <strong>TSV</strong> or{' '}
        <a href="https://parquet.apache.org/" target="_blank" rel="noopener noreferrer">
          Parquet
        </a>{' '}
        files. Available for <strong>PREMIUM</strong> profiles. For licensing, contact{' '}
        <a href="mailto:info@disgenet.com">info@disgenet.com</a>.
      </p>

      <div className="divider" />

      <div className="mt-4">
        <h5 className="row">
          <ArrowDownTrayIcon className="w-6 h-6 mr-2 text-pink" />
          Download files
        </h5>
        <p>
          Your API key is prefilled from your account. You can also paste another key if needed. Manage it in{' '}
          <Link to="/Profile-area">your profile</Link>.
        </p>

        <div className="dump-api-row">
          <input
            type={isApiKeyVisible ? 'text' : 'password'}
            value={apiKey}
            onChange={e => {
              setApiKey(e.target.value);
              if (downloadError) setDownloadError(null);
            }}
            placeholder="API key"
            aria-label="API key"
            autoComplete="off"
          />
          <button
            type="button"
            className="dump-icon-btn"
            onClick={() => setIsApiKeyVisible(prev => !prev)}
            aria-label={isApiKeyVisible ? 'Hide API key' : 'Show API key'}
          >
            {isApiKeyVisible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
          </button>
        </div>
        {downloadError && <p className="dump-error">{downloadError}</p>}

        <div className="dump-feature-grid">
          {FORMATS.map(format => {
            const Icon = format.icon;
            return (
              <form
                key={format.id}
                className="dump-feature-card"
                onSubmit={event => handleDownload(event, format.endpoint)}
              >
                <Icon className="w-5 h-5 text-pink" />
                <h3>{format.title}</h3>
                <p>{format.description}</p>
                <p className="dump-md5">
                  md5: <code>{DUMP_MD5[format.id]}</code>
                </p>
                <button type="submit" className="btn-gradient-pink dump-download-btn">
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Download {format.id === 'tsv' ? 'TSV' : 'Parquet'}
                </button>
              </form>
            );
          })}
        </div>
      </div>

      <div className="divider" />

      <div className="mt-4 mb-3">
        <h5 className="row">
          <CommandLineIcon className="w-6 h-6 mr-2 text-pink" />
          Download from the command line
        </h5>
        <p>
          Replace <code>YOUR_API_KEY</code> with your key, then run:
        </p>
        {curlCommands.map(cmd => (
          <CopyableCode key={cmd.id} value={cmd.value} label={cmd.label} />
        ))}
      </div>

      <div className="divider" />

      <div className="mt-4">
        <h5 className="row">
          <ShieldCheckIcon className="w-6 h-6 mr-2 text-pink" />
          Verify the download
        </h5>
        <p>Compare the MD5 of the downloaded archive with the checksum shown above.</p>

        <h6 className="font-semibold mt-3 mb-1">Windows</h6>
        <div className="dump-steps">
          <div className="dump-step">
            <span className="dump-step__num">01</span>
            <p>
              Open Command Prompt and go to the folder with the <code>.tar.gz</code> file.
            </p>
          </div>
          <div className="dump-step">
            <span className="dump-step__num">02</span>
            <p>
              Run <code>CertUtil -hashfile yourfile.tar.gz MD5</code> (
              <a
                href="https://learn.microsoft.com/en-us/windows-server/administration/windows-commands/certutil"
                target="_blank"
                rel="noopener noreferrer"
              >
                CertUtil docs
              </a>
              ).
            </p>
          </div>
          <div className="dump-step">
            <span className="dump-step__num">03</span>
            <p>Compare the output with the expected MD5 hash.</p>
          </div>
        </div>

        <h6 className="font-semibold mt-4 mb-1">macOS and Linux</h6>
        <div className="dump-steps">
          <div className="dump-step">
            <span className="dump-step__num">01</span>
            <p>
              Open Terminal and go to the folder with the <code>.tar.gz</code> file.
            </p>
          </div>
          <div className="dump-step">
            <span className="dump-step__num">02</span>
            <p>
              Run <code>md5 yourfile.tar.gz</code> (macOS) or <code>md5sum yourfile.tar.gz</code> (Linux).
            </p>
          </div>
          <div className="dump-step">
            <span className="dump-step__num">03</span>
            <p>Compare the output with the expected MD5 hash.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
